/**
 * useCalendarUtilization — the five-month utilization series, straight off the
 * Manpower calendar.
 *
 * Replaces the utilization_monthly workbook table as the chart's source: that
 * was a monthly export somebody had to remember to refresh, while the calendar
 * is what the team keeps current. The maths (PTO and weekends excluded, NA
 * only, no interns, two months back to two forward) lives in
 * utils/utilizationFromCalendar so it can be tested without a browser.
 *
 * `edits` and `details` (each scheduled day's Status / Location / Project #)
 * are handed back too, so the Utilization Forecast counts its capacity off the
 * same fetch instead of making a second one.
 *
 * `failed` is true when the calendar could not be read (an error answer or no
 * answer). The charts then say so instead of drawing an empty calendar as a
 * confident 0% booked and full capacity with no PTO.
 *
 * Someone's calendar save (r50 live events, kind 'manpower') re-reads it, and
 * so does an engineer change (kind 'engineers'): the two move together - a
 * calendar save rewrites the engineer cards, and the cards say who counts.
 * One re-read per burst of both. Quietly: a re-read that fails keeps the
 * figures on screen rather than turning them into `failed`.
 *
 * The calendar's tables (/api/manpower/tables, 10/16/26) are read alongside:
 * only engineers in a table that counts toward utilization are counted, so
 * `layout` is handed back for the forecast too. A tables read that fails is
 * `failed` like the calendar's - guessing who counts would draw a wrong line.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApp } from '../context/AppContext';
import { monthlyUtilization } from '../utils/utilizationFromCalendar';
import { DEFAULT_LAYOUT, layoutOf } from '../components/manpower/calendarTables';
import { latestReads, useLiveRefresh } from '../live/useLive';

// One empty map, so a caller memoising on edits / details is not re-run on
// every render while the fetch is in flight.
const NONE = Object.freeze({});

/** What the utilization charts show in place of a chart when `failed`. */
export const CALENDAR_FAILED = 'Could not load the calendar - reload to see utilization.';

export function useCalendarUtilization() {
  const { allEngineers } = useApp();
  // { edits, details, failed }; null until the fetch settles
  const [cal, setCal] = useState(null);
  const alive = useRef(true);
  const [reads] = useState(latestReads); // the first read and live re-reads can overlap

  // `quiet` (a live re-read): a failure leaves what is shown as it is
  const read = useCallback(
    (quiet) => {
      const t = reads.start();
      const take = (value) => {
        if (alive.current && reads.show(t)) setCal(value);
      };
      const failed = () => {
        if (quiet !== true) take({ edits: NONE, details: NONE, layout: DEFAULT_LAYOUT, failed: true });
      };
      return Promise.all([fetch('/api/manpower/edits'), fetch('/api/manpower/tables')])
        .then(([r, rt]) => {
          if (!r || !r.ok || !rt || !rt.ok) return failed();
          return Promise.all([r.json(), rt.json()]).then(([d, t]) =>
            take({
              edits: (d && d.edits) || {},
              details: (d && d.details) || {},
              layout: layoutOf(t),
              failed: false,
            })
          );
        })
        .catch(failed);
    },
    [reads]
  );

  useEffect(() => {
    alive.current = true;
    read();
    return () => {
      alive.current = false;
    };
  }, [read]);
  const reReadQuietly = useCallback(() => read(true), [read]);
  useLiveRefresh(['manpower', 'engineers'], reReadQuietly);

  const failed = !!(cal && cal.failed);
  return {
    // Empty until the calendar answers, and when it could not be read, so the
    // chart never draws a confident 0% that is really "no data".
    data:
      cal === null || failed
        ? []
        : monthlyUtilization({ engineers: allEngineers, edits: cal.edits, details: cal.details, layout: cal.layout }),
    loaded: cal !== null,
    failed,
    edits: cal ? cal.edits : NONE,
    details: cal ? cal.details : NONE,
    layout: cal ? cal.layout : DEFAULT_LAYOUT,
  };
}
